import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ColumnVisibilityToggle from '../../../community/residents/components/ColumnVisibilityToggle.jsx';
import FilterPopover from '../../../community/residents/components/FilterPopover.jsx';
import ActiveFilterBar from '../../../community/residents/components/ActiveFilterBar.jsx';
import { exportPreformNotesCsv } from '../../../../services/csvExport.js';
import usePersistedColumns from '../../../../hooks/usePersistedColumns.js';

/**
 * Column definitions for the note templates table.
 * Each column has: key, label, sortable, filterable, required (can't hide).
 */
const NOTE_COLUMN_DEFS = [
    { key: 'preform_note', label: 'Note Template', sortable: true, filterable: true, required: true },
    { key: 'create_time', label: 'Initially Added', sortable: true, filterable: false, required: false },
    { key: 'last_modified', label: 'Last Updated', sortable: true, filterable: false, required: false },
    { key: 'display_name', label: 'Last Updated By', sortable: true, filterable: true, required: false },
];

const DEFAULT_VISIBLE = ['preform_note', 'create_time', 'last_modified', 'display_name'];
const ALL_COLUMN_KEYS = NOTE_COLUMN_DEFS.map((c) => c.key);

/** Column labels for ActiveFilterBar */
const COLUMN_LABELS = {};
NOTE_COLUMN_DEFS.forEach((col) => { COLUMN_LABELS[col.key] = col.label; });

// ---------------------------------------------------------------------------
// Row-level kebab menu
// ---------------------------------------------------------------------------
const NoteRowActions = ({ note, onEdit, onDelete }) => {
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    return (
        <div className="ap-row-actions" ref={menuRef}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="ap-row-actions__trigger"
                aria-label={`Actions for template`}
                aria-expanded={open}
            >
                <i className="fe fe-more-vertical" aria-hidden="true" />
            </button>

            {open ? (
                <div className="ap-row-actions__menu">
                    <button
                        type="button"
                        onClick={() => { onEdit(note); setOpen(false); }}
                        className="ap-row-actions__item"
                    >
                        <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                        </svg>
                        Edit Template
                    </button>
                    <button
                        type="button"
                        onClick={() => { onDelete(note); setOpen(false); }}
                        className="ap-row-actions__item ap-row-actions__item--danger"
                    >
                        <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                        Delete Template
                    </button>
                </div>
            ) : null}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const PreformNotesTab = ({
    notes,
    onAddNote,
    onEditNote,
    onDeleteNotes,
    onImportFromCampus,
    saving,
    tzOffset,
}) => {
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [sortColumn, setSortColumn] = useState('preform_note');
    const [sortOrder, setSortOrder] = useState('asc');
    const [filters, setFilters] = useState({});
    const [visibleColumns, handleColumnToggle] = usePersistedColumns('settings-preform-notes', DEFAULT_VISIBLE, ALL_COLUMN_KEYS);

    // Clear selection on data reload
    useEffect(() => {
        setSelectedIds([]);
    }, [notes]);

    /** Convert UTC timestamp to local display using campus tzOffset */
    const formatDate = useCallback((utcStr) => {
        if (!utcStr) return '—';
        try {
            const d = new Date(utcStr);
            const offset = parseInt(tzOffset) || 0;
            d.setHours(d.getHours() + offset);
            return d.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
            });
        } catch {
            return utcStr;
        }
    }, [tzOffset]);

    // Active columns based on visibility
    const activeColumns = useMemo(
        () => NOTE_COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key)),
        [visibleColumns]
    );

    // Collect unique filter values from the full (unfiltered) dataset
    const getFilterValues = useCallback(
        (columnKey) => {
            const values = new Set();
            (notes || []).forEach((row) => {
                const val = row[columnKey];
                values.add(val != null && val !== '' ? String(val) : '(empty)');
            });
            return [...values].sort((a, b) =>
                a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
            );
        },
        [notes]
    );

    // ======================================================================
    // Filtered + sorted data pipeline
    // ======================================================================
    const displayData = useMemo(() => {
        let data = [...(notes || [])];

        // 1. Search
        if (search.trim()) {
            const term = search.toLowerCase();
            data = data.filter(
                (n) =>
                    (n.preform_note || '').toLowerCase().includes(term) ||
                    (n.display_name || '').toLowerCase().includes(term)
            );
        }

        // 2. Column filters
        Object.entries(filters).forEach(([columnKey, values]) => {
            if (!values || values.length === 0) return;
            data = data.filter((row) => {
                const val = row[columnKey];
                const strVal = val != null && val !== '' ? String(val) : '(empty)';
                return values.includes(strVal);
            });
        });

        // 3. Sort
        data.sort((a, b) => {
            const aVal = a[sortColumn] || '';
            const bVal = b[sortColumn] || '';
            const result = String(aVal).localeCompare(String(bVal), undefined, {
                numeric: true,
                sensitivity: 'base',
            });
            return sortOrder === 'asc' ? result : -result;
        });

        return data;
    }, [notes, search, filters, sortColumn, sortOrder]);

    // ======================================================================
    // Selection
    // ======================================================================
    const allSelected = displayData.length > 0 && displayData.every((r) => selectedIds.includes(r.preform_note_id));
    const someSelected = selectedIds.length > 0 && !allSelected;

    const handleSelectRow = useCallback((id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    }, []);

    const handleSelectAll = useCallback(() => {
        const allIds = displayData.map((r) => r.preform_note_id);
        const currentAllSelected = allIds.every((id) => selectedIds.includes(id));
        if (currentAllSelected) {
            setSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
        } else {
            setSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
        }
    }, [displayData, selectedIds]);

    // ======================================================================
    // Sort / Filter / Column handlers
    // ======================================================================
    const handleSort = useCallback((column) => {
        if (sortColumn === column) {
            setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(column);
            setSortOrder('asc');
        }
    }, [sortColumn]);

    const handleFilterChange = useCallback((columnKey, values) => {
        setFilters((prev) => ({ ...prev, [columnKey]: values }));
    }, []);

    const handleRemoveFilter = useCallback((columnKey, value) => {
        setFilters((prev) => ({
            ...prev,
            [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
        }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({});
        setSearch('');
    }, []);

    // ======================================================================
    // Actions
    // ======================================================================
    const handleBulkDelete = () => {
        if (selectedIds.length === 0) return;
        onDeleteNotes(selectedIds);
    };

    const handleSingleDelete = (note) => {
        onDeleteNotes([note.preform_note_id]);
    };

    /** Prepare rows with formatted dates for CSV export */
    const prepareExportRows = useCallback((rows) => {
        return rows.map((r) => ({
            ...r,
            _create_time: formatDate(r.create_time),
            _last_modified: formatDate(r.last_modified),
        }));
    }, [formatDate]);

    const handleExportAll = useCallback(() => {
        if (displayData.length) exportPreformNotesCsv(prepareExportRows(displayData));
    }, [displayData, prepareExportRows]);

    const handleBulkExport = useCallback(() => {
        const selected = (notes || []).filter((n) => selectedIds.includes(n.preform_note_id));
        if (selected.length) exportPreformNotesCsv(prepareExportRows(selected), 'note-templates-selected.csv');
    }, [selectedIds, notes, prepareExportRows]);

    // ======================================================================
    // Sort icons
    // ======================================================================
    const renderSortIcon = (columnKey) => {
        if (sortColumn !== columnKey) {
            return (
                <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
                </svg>
            );
        }
        return sortOrder === 'asc' ? (
            <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
            </svg>
        ) : (
            <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
        );
    };

    /** Render a single cell value */
    const renderCell = (row, columnKey) => {
        switch (columnKey) {
            case 'create_time':
                return <span className="ap-cell-text">{formatDate(row.create_time)}</span>;
            case 'last_modified':
                return <span className="ap-cell-text">{formatDate(row.last_modified)}</span>;
            case 'display_name':
                return <span className="ap-cell-text">{row.display_name || '—'}</span>;
            default:
                return <span className="ap-cell-text">{row[columnKey] || '—'}</span>;
        }
    };

    // ======================================================================
    // Render
    // ======================================================================
    const hasFilters = search.trim() || Object.values(filters).some((v) => v.length > 0);

    return (
        <>
            {/* Controls row */}
            <div className="ap-controls">
                <div className="ap-controls__left">
                    <div className="ap-search">
                        <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                        <input
                            type="search"
                            placeholder="Search templates..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="ap-search__input"
                            aria-label="Search templates"
                        />
                    </div>
                </div>
                <div className="ap-controls__right">
                    <ColumnVisibilityToggle
                        columns={NOTE_COLUMN_DEFS}
                        visibleColumns={visibleColumns}
                        onToggle={handleColumnToggle}
                    />
                    <button type="button" className="ap-btn ap-btn--secondary" onClick={handleExportAll} aria-label="Export templates">
                        <svg className="ap-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                        </svg>
                        Export
                    </button>
                    {onImportFromCampus ? (
                        <button type="button" className="ap-btn ap-btn--secondary" onClick={onImportFromCampus} aria-label="Import from campus">
                            <svg className="ap-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                            </svg>
                            Import from Campus
                        </button>
                    ) : null}
                    <button type="button" onClick={onAddNote} className="ap-btn ap-btn--primary" aria-label="Add new template">
                        <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                        Add Template
                    </button>
                </div>
            </div>

            {/* Active filter chips */}
            <ActiveFilterBar
                filters={filters}
                columnLabels={COLUMN_LABELS}
                onRemoveFilter={handleRemoveFilter}
                onClearAll={handleClearAllFilters}
            />

            {/* Bulk action bar */}
            {selectedIds.length > 0 ? (
                <div className="ap-bulk-bar">
                    <div className="ap-bulk-bar__left">
                        <span className="ap-bulk-bar__count">
                            {selectedIds.length} template{selectedIds.length !== 1 ? 's' : ''} selected
                        </span>
                        <button type="button" onClick={() => setSelectedIds([])} className="ap-bulk-bar__deselect">
                            Deselect All
                        </button>
                    </div>
                    <div className="ap-bulk-bar__right">
                        <button type="button" onClick={handleBulkExport} className="ap-bulk-btn" aria-label="Export selected templates">
                            <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                            </svg>
                            Export
                        </button>
                        <button type="button" onClick={handleBulkDelete} disabled={saving} className="ap-bulk-btn ap-bulk-btn--danger" aria-label="Delete selected templates">
                            <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                            </svg>
                            Delete
                        </button>
                    </div>
                </div>
            ) : null}

            {/* Table */}
            {displayData.length === 0 ? (
                <div className="ss-empty" style={{ marginTop: 'var(--space-4)' }}>
                    <p className="ss-empty__text">
                        {hasFilters
                            ? 'No templates match your filters.'
                            : 'No note templates configured.'}
                    </p>
                </div>
            ) : (
                <div className="ap-table-wrap">
                    <table className="ap-table">
                        <thead className="ap-thead">
                            <tr>
                                {/* Checkbox column */}
                                <th scope="col" className="ap-th ap-th--checkbox">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                        onChange={handleSelectAll}
                                        className="ap-checkbox"
                                        aria-label="Select all templates"
                                    />
                                </th>

                                {/* Data columns */}
                                {activeColumns.map((col) => (
                                    <th key={col.key} scope="col" className="ap-th">
                                        <div className="ap-th__content">
                                            {col.sortable ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleSort(col.key)}
                                                    className="ap-th__sort-btn"
                                                    aria-label={`Sort by ${col.label}`}
                                                >
                                                    {col.label}
                                                    {renderSortIcon(col.key)}
                                                </button>
                                            ) : (
                                                <span>{col.label}</span>
                                            )}

                                            {col.filterable ? (
                                                <FilterPopover
                                                    columnKey={col.key}
                                                    label={col.label}
                                                    values={getFilterValues(col.key)}
                                                    selectedValues={filters[col.key] || []}
                                                    onChange={handleFilterChange}
                                                />
                                            ) : null}
                                        </div>
                                    </th>
                                ))}

                                {/* Actions column */}
                                <th scope="col" className="ap-th ap-th--actions">Edit</th>
                            </tr>
                        </thead>

                        <tbody className="ap-tbody">
                            {displayData.map((row) => {
                                const isSelected = selectedIds.includes(row.preform_note_id);
                                return (
                                    <tr
                                        key={row.preform_note_id}
                                        className={`ap-tr ${isSelected ? 'is-selected' : ''}`}
                                    >
                                        {/* Checkbox */}
                                        <td className="ap-td ap-td--checkbox">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => handleSelectRow(row.preform_note_id)}
                                                className="ap-checkbox"
                                                aria-label={`Select template`}
                                            />
                                        </td>

                                        {/* Data cells */}
                                        {activeColumns.map((col) => (
                                            <td key={col.key} className="ap-td">
                                                {renderCell(row, col.key)}
                                            </td>
                                        ))}

                                        {/* Row actions kebab */}
                                        <td className="ap-td ap-td--actions">
                                            <NoteRowActions
                                                note={row}
                                                onEdit={onEditNote}
                                                onDelete={handleSingleDelete}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

export default PreformNotesTab;
